import {useContext} from 'react';

import {useTranslation} from 'react-i18next';

import {Box, Divider, Grid, Typography} from '@mui/material';

import {BatchInvitationProgressDetailsResponse} from '../types/screeningExternalApplicantsTypes';
import {normalizeBatchInvitationErrorMessage} from '../utils/batchInvitationsErrorUtils';
import {
  getBatchInvitationStatusTranslationKey,
  getLocalizedDepartment,
  getUserInitials,
  hasCompletedWithIssues,
} from '../utils/batchInvitationsProgressUtils';

import {BatchInvitationDetailsInfoRow} from './BatchInvitationDetailsInfoRow';
import EmpionDialog from '../../../../components/EmpionDialog/EmpionDialog';
import {LoadingContainer} from '../../../../components/Loading/LoadingContainer';
import {LanguageContext} from '../../../../contexts/LanguageContext/LanguageContext';
import {transformDateWithTime} from '../../../../utils/dateUtils/dateFormattingUtils';

interface Props {
  open: boolean;
  onClose: () => void;
  details?: BatchInvitationProgressDetailsResponse;
  isLoading: boolean;
  error?: unknown;
}

export const BatchInvitationDetailsModal = ({open, onClose, details, isLoading, error}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'screeningModule.batchInvitationsProgress.details'});
  const {t: tStatus} = useTranslation('translation', {keyPrefix: 'screeningModule.batchInvitationsProgress.statuses'});
  const currentLanguage = useContext(LanguageContext);
  const detailsErrorOptions = {
    genericErrorMessage: t('errors.generic'),
    malformedCsvErrorMessage: t('errors.malformedCsv'),
    formatMalformedCsvLineErrorMessage: (line: string) => t('errors.malformedCsvWithLine', {line}),
    confirmStateErrorMessage: t('errors.confirmState'),
    rowGenericErrorMessage: t('errors.rowGeneric'),
  };
  const loadErrorOptions = {
    ...detailsErrorOptions,
    genericErrorMessage: t('errors.loadFailed'),
    rowGenericErrorMessage: t('errors.loadFailed'),
  };
  const detailsErrorMessage = normalizeBatchInvitationErrorMessage(
    (error as {extra_details?: string; error_message?: string})?.extra_details ||
      (error as {extra_details?: string; error_message?: string})?.error_message ||
      t('errors.loadFailed'),
    loadErrorOptions
  );
  const metadata = details?.metadata;
  const context = details?.context;
  const counters = details?.counters;
  const normalizedRowErrors =
    (details?.rowErrors ?? []).map((rowError) => ({
      ...rowError,
      errors: (rowError.errors ?? []).map((entry) =>
        normalizeBatchInvitationErrorMessage(entry, detailsErrorOptions, 'row')
      ),
    })) || [];
  const normalizedDetailsErrorMessage = details?.errorMessage
    ? normalizeBatchInvitationErrorMessage(details.errorMessage, detailsErrorOptions)
    : null;
  const createdBy = metadata?.createdBy || null;
  const createdByInitials = getUserInitials(createdBy);

  return (
    <EmpionDialog
      open={open}
      onClose={onClose}
      title={t('title')}
      showCloseButton
      backgroundColor='#fff'
      sx={{
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            width: '100%',
            maxWidth: '736px',
            padding: '24px',
            borderRadius: '16px',
          },
        },
      }}
    >
      {isLoading ? (
        <LoadingContainer minHeight='220px' />
      ) : error ? (
        <Typography variant='body2' color='error'>
          {detailsErrorMessage}
        </Typography>
      ) : !details ? (
        <Typography variant='body2'>{t('empty')}</Typography>
      ) : (
        <Box>
          <Grid container spacing={2}>
            <BatchInvitationDetailsInfoRow label={t('batchId')} value={metadata?.batchId || '-'} />
            <BatchInvitationDetailsInfoRow
              label={t('createdAt')}
              value={metadata?.createdAt ? transformDateWithTime(metadata.createdAt) : '-'}
            />
            <BatchInvitationDetailsInfoRow
              label={t('createdBy')}
              value={
                createdBy ? (
                  <Box display='flex' alignItems='center' gap='8px'>
                    <Box
                      sx={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        backgroundColor: 'rgba(31, 45, 51, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Typography variant='body2' sx={{fontSize: '12px', fontWeight: 700}}>
                        {createdByInitials}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant='body2'>
                        {`${createdBy.firstName || ''} ${createdBy.lastName || ''}`.trim() || t('createdByUnknown')}
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        {createdBy.email}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Typography variant='body2'>{t('createdByUnknown')}</Typography>
                )
              }
            />
            <BatchInvitationDetailsInfoRow label={t('jobPosting')} value={context?.jobPosting || '-'} />
            <BatchInvitationDetailsInfoRow label={t('location')} value={context?.location || '-'} />
            <BatchInvitationDetailsInfoRow
              label={t('department')}
              value={getLocalizedDepartment(context?.department || null, currentLanguage)}
            />
            <BatchInvitationDetailsInfoRow
              label={t('status')}
              value={tStatus(getBatchInvitationStatusTranslationKey(details.status))}
            />
            <BatchInvitationDetailsInfoRow
              label={t('processedAt')}
              value={details.processedAt ? transformDateWithTime(details.processedAt) : '-'}
            />
            <BatchInvitationDetailsInfoRow label={t('totalRows')} value={String(counters?.totalRowsCount ?? 0)} />
            <BatchInvitationDetailsInfoRow
              label={t('processedRows')}
              value={String(counters?.processedRowsCount ?? 0)}
            />
            <BatchInvitationDetailsInfoRow label={t('successRows')} value={String(counters?.successRowsCount ?? 0)} />
            <BatchInvitationDetailsInfoRow label={t('failedRows')} value={String(counters?.failedRowsCount ?? 0)} />
          </Grid>

          {hasCompletedWithIssues(details) && (
            <Typography variant='body2' marginTop={2} color='warning.main'>
              {t('completedWithIssues')}
            </Typography>
          )}

          {normalizedDetailsErrorMessage && (
            <Typography variant='body2' marginTop={1} color='error'>
              {normalizedDetailsErrorMessage}
            </Typography>
          )}

          <Divider sx={{marginY: 2}} />
          <Typography variant='h4' marginBottom={1}>
            {t('rowErrorsTitle')}
          </Typography>
          {normalizedRowErrors.length === 0 ? (
            <Typography variant='body2'>{t('noRowErrors')}</Typography>
          ) : (
            normalizedRowErrors.map((rowError) => (
              <Typography key={rowError.row} variant='body2' marginBottom={0.5}>
                {t('rowError', {row: rowError.row, errors: rowError.errors.join(', ')})}
              </Typography>
            ))
          )}
        </Box>
      )}
    </EmpionDialog>
  );
};
